import { RiskMatrixTypeResult } from "app/area/case-report/risk-listing/types-store";
import { riskReportAttributeMappings } from "app/area/reports/containers/chart/components/attributes/constants";
import { DataLabelsOptions, PointOptionsObject } from "highcharts";
import get from "lodash/get";
import * as navigate from 'app/area/shared/navigation';
import { OnSelectedCell } from "app/components/risk-matrix";
import styles from './risk-matrix-listing.scss';
import { FavouriteLayoutAttributes } from 'app/components/favourite-layout/types';
import { RiskMatrixCellProperties } from "app/area/case-report/risk-listing/constants";
import { isCaseViewModalEnabled, toAbsoluteUrl } from "app/infrastructure/hosting";

export const setRiskReportOptionsSeries = (
  optionSeries: Highcharts.SeriesOptionsRegistry['SeriesHeatmapOptions'],
  layoutAttributes: FavouriteLayoutAttributes,
  riskMatrixTypeResult: RiskMatrixTypeResult,
  locale: string,
  onSelectedCell: OnSelectedCell,
  onOpenModal: (xAxisItemIndex: number, yAxisItemIndex: number) => void
) : Highcharts.SeriesOptionsRegistry['SeriesHeatmapOptions'] => ({
  ...optionSeries,
  point: {
    ...optionSeries.point,
    events: {
      ...optionSeries.point?.events,
      click: (event: Highcharts.PointClickEventObject) => {
        event?.preventDefault();
        const { target } = event as Highcharts.PointClickEventObject & { target: Element };
        const element = target as HTMLElement;
        const modalTrigger = element.closest('[data-value="modal"]');

        if (modalTrigger) {
          onOpenModal(event.point.x, event.point.y);
          return;
        }
        // Check if the element has a class like "case-number-2370"
        const caseNumberClass = Array.from(element.classList).find(cls => cls.startsWith('case-number-'));
        if (caseNumberClass) {
          const caseNumber = Number(caseNumberClass.replace('case-number-', ''));
          !!caseNumber && (isCaseViewModalEnabled() ? navigate.toCaseViewModal(caseNumber) : navigate.toCaseView(caseNumber));
        } else {
          onSelectedCell(event.point.x, event.point.y);
        }
      }
    }
  },
  data: (optionSeries.data || []).map((dataPoint) => {
    const pointOptions = dataPoint as PointOptionsObject;
    const x = pointOptions.x;
    const y = pointOptions.y;
    const cellDataCount = riskMatrixTypeResult?.cells['x' + x + 'y' + y]?.data?.count;
    const cellDataCases = riskMatrixTypeResult?.cells['x' + x + 'y' + y]?.data?.cases || [];
    const style = (pointOptions.dataLabels as DataLabelsOptions)?.style;
    const getLayoutAttributesStyleItem = (item: string): string =>
      String(get(layoutAttributes, 'Highcharts.plotOptions.heatmap.dataLabels.style.' + item));
    const isShowCaseNumbersEnabled = !!layoutAttributes[riskReportAttributeMappings.showCaseNumbers] && !!cellDataCases?.length;

    return {
      ...pointOptions,
      value: cellDataCount || 0,
      selected: !cellDataCount,
      dataLabels: {
        ...pointOptions.dataLabels,
        allowOverlap: true,
        useHTML: true,
        ...(isShowCaseNumbersEnabled && { inside: true, align: 'center', verticalAlign: 'middle' }),
        style: {
          ...style,
          fontSize: getLayoutAttributesStyleItem('fontSize') ?? style.fontSize,
          fontWeight: getLayoutAttributesStyleItem('fontWeight') ?? style.fontWeight,
          textOutline: getLayoutAttributesStyleItem('textOutline') ?? style.textOutline
        },
        formatter: function () {
          if (isShowCaseNumbersEnabled) {
            const { shapeArgs: cellShape, value } = this;
            const hideCaseNumbers = cellShape?.width <= RiskMatrixCellProperties.MinCellWidth;
            const caseNumberList = hideCaseNumbers ? '' :
              cellDataCases.slice(0, RiskMatrixCellProperties.MaxCasesPerCell).reduce((acc, cn) => {
                const caseNumber = Number(cn);
                const caseNumberLink = toAbsoluteUrl(`/case/${caseNumber}`);
                return acc + `<a href="${caseNumberLink}" class="${styles.caseNumberCell} case-number-${caseNumber}">${caseNumber}</a>`;
                }, '');
            const hideMoreCasesButton = cellDataCases.length <= 2 && cellShape?.width > RiskMatrixCellProperties.MinCellWidth;
            const moreCasesButton = `<span data-value="modal" class="${styles.caseNumberCell} ${styles.moreCases}">
              <img src="${toAbsoluteUrl('/i/list.svg')}" /></span>`;

            const caseNumberListContainer = cellDataCases.length > 0 ?
              `<div class="${styles.caseNumberList}">
                ${hideMoreCasesButton ? caseNumberList : moreCasesButton + caseNumberList}
              </div>`
              : '';

            const casesCount = value > 0 ?
              `<div class="${styles.caseCount}">${Number(value)}</div>` : '';

            const stepHeightRemainder = (cellShape?.height - RiskMatrixCellProperties.CaseCountHeight)
              % RiskMatrixCellProperties.CaseNumberLineHeight;
            const calculatedStepHeight = cellShape?.height - stepHeightRemainder - RiskMatrixCellProperties.MarginBottom;

            return `<div style="width: ${cellShape?.width}px; height: ${cellShape?.height}px"
              class="${styles.casesContainer}">
                <div class="${styles.caseOverflowContainer}" style="height: ${calculatedStepHeight}px;">
                  ${casesCount}
                  ${caseNumberListContainer}
                </div>
              </div>`;
          }

          return this.value ? new Intl.NumberFormat(locale).format(this.value) : '';
        }
      }
    };
  })
});
